import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useThemeColors } from '@/hooks/useThemeColors';
import ClassLayout from '@/layouts/ClassLayout';
import { AmaRecord, AmaSeries, CourseData } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { ChevronLeft, ChevronRight, List, Play, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoPlayer from './ClassDetail/Partials/VideoPlayer';

type Props = {
    course: CourseData;
    ama_series: AmaSeries;
    record: AmaRecord;
};

interface AmaRecordSidebarProps {
    course: CourseData;
    amaSeries: AmaSeries;
    currentRecordId: number;
}

const AmaRecordSidebar: React.FC<AmaRecordSidebarProps> = ({
    course,
    amaSeries,
    currentRecordId,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    const records = amaSeries.records || [];
    const currentIndex = records.findIndex((r) => r.id === currentRecordId);
    const previousRecord = currentIndex > 0 ? records[currentIndex - 1] : null;
    const nextRecord = currentIndex < records.length - 1 ? records[currentIndex + 1] : null;

    return (
        <div className="flex h-full w-full flex-col border-l border-gray-200 bg-white text-gray-900 lg:w-80">
            {/* Sidebar Header */}
            <div className="border-b border-gray-200 p-6">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                        {t('course.amaRecordSidebar', 'Sesiones AMA')}
                    </h2>
                    <Link
                        href={`/courses/${course.slug}/ama-series/`}
                        className="text-sm text-gray-500 hover:text-gray-700"
                    >
                        {t('course.viewAll', 'Ver todas')}
                    </Link>
                </div>
                <div className="text-sm text-gray-600">
                    <div className="font-medium">{amaSeries.title}</div>
                </div>
            </div>

            {/* Records List */}
            <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                    <div className="mb-4 text-sm text-gray-500">
                        {records.length} {t('course.sessions', 'sesiones')}
                    </div>

                    <div className="space-y-2">
                        {records.map((record) => {
                            const isCurrent = record.id === currentRecordId;

                            return (
                                <Link
                                    key={record.id}
                                    href={`/courses/${course.slug}/ama-series/${amaSeries.id}/records/${record.id}/`}
                                    className={`block rounded-lg border p-3 transition-all duration-200 ${
                                        isCurrent
                                            ? `${colors.border} ${colors.accentBg} text-gray-900`
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                            <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${isCurrent ? colors.border : 'border-gray-300'}`}>
                                                {isCurrent && (
                                                    <Play
                                                        size={10}
                                                        fill="currentColor"
                                                        className={colors.iconColor}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate text-sm font-medium">
                                                {record.title}
                                            </div>
                                            {isCurrent && (
                                                <div className={`mt-1 text-xs ${colors.iconColor}`}>
                                                    {t('course.currentlyWatching', 'Reproduciendo')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Navigation Footer */}
            <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                    {previousRecord ? (
                        <Link
                            href={`/courses/${course.slug}/ama-series/${amaSeries.id}/records/${previousRecord.id}/`}
                            className="flex items-center space-x-2 text-gray-600 transition-colors hover:text-gray-900"
                        >
                            <ChevronLeft size={16} />
                            <span className="text-sm font-medium">
                                {t('course.previous', 'Anterior')}
                            </span>
                        </Link>
                    ) : (
                        <div></div>
                    )}

                    {nextRecord && (
                        <Link
                            href={`/courses/${course.slug}/ama-series/${amaSeries.id}/records/${nextRecord.id}/`}
                            className="btn-gradient flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium"
                        >
                            <span>{t('course.next', 'Siguiente')}</span>
                            <ChevronRight size={16} />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

const AmaRecordDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { props } = usePage<Props>();
    const { course, ama_series, record } = props;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    return (
        <ClassLayout
            courseTitle={course.title}
            courseSlug={course.slug}
            backUrl={`/courses/${course.slug}/ama-series/`}
        >
            <PageTitle title={record.title} />
            <div className="relative flex h-[calc(100vh-57px)]">
                {/* Main Content Area */}
                <div className="flex flex-1 flex-col overflow-y-auto">
                    {/* Video Player */}
                    <VideoPlayer
                        videoUrl={record.bunny_url}
                        vimeoUrl={record.vimeo_url}
                    />

                    {/* Record Info */}
                    <div className="flex-1 bg-gray-900 p-6">
                        <div className="mx-auto max-w-4xl">
                            <h1 className="mb-4 text-2xl font-bold text-white">
                                {record.title}
                            </h1>
                            {record.description && (
                                <div
                                    className="prose prose-invert max-w-none text-gray-300"
                                    dangerouslySetInnerHTML={{ __html: record.description }}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Mobile Sidebar Toggle Button */}
                <button
                    onClick={() => setIsSidebarOpen(true)}
                    className="btn-gradient fixed right-6 bottom-6 z-40 flex h-14 w-14 items-center justify-center rounded-full lg:hidden"
                    aria-label={t('course.showCourseContent')}
                >
                    <List size={24} />
                </button>

                {/* Mobile Sidebar Overlay */}
                {isSidebarOpen && (
                    <div
                        className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <div
                    className={`fixed top-0 right-0 z-50 h-full w-80 transform transition-transform duration-300 lg:relative lg:z-auto lg:translate-x-0 ${
                        isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
                >
                    {/* Mobile Close Button */}
                    <button
                        onClick={() => setIsSidebarOpen(false)}
                        className="absolute top-4 left-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 lg:hidden"
                        aria-label={t('course.closeSidebar')}
                    >
                        <X size={18} />
                    </button>
                    <AmaRecordSidebar
                        course={course}
                        amaSeries={ama_series}
                        currentRecordId={record.id}
                    />
                </div>
            </div>
        </ClassLayout>
    );
};

const AmaRecordDetail: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <AmaRecordDetailContent />
        </ThemeWrapper>
    );
};

export default AmaRecordDetail;
